import { useEffect, useState } from "react";
import { imageUrl } from "../../utils/imageUrl";
import { Link } from "react-router-dom";
import Reveal from "../common/Reveal";
import { fetchCategoryTree } from "../../api/categories";
import { API_BASE as API, cachedFetch } from "../../api/api";
import { getSessionId } from "../../utils/analytics";
import "./ShopByCategory.css";
import { isInStock } from "../../utils/stock";
import { DRINKS_SLUG, isDrinksWindow, serverNow } from "../../utils/drinksWindow";

const SLIDE_INTERVAL = 6000;

const FEATURED_COUNT = 4;
const MIN_PRODUCTS_TO_FEATURE = 4;
const MAX_SLIDES_PER_CARD = 10;
const EPOCH_MS = 6 * 60 * 60 * 1000; // 6 hours — which departments are featured changes on this cadence

// Small deterministic PRNG (mulberry32) seeded from a string — same spirit
// as the epoch+session seeding in backend/app/rails.py, but client-side
// here since this is a decorative homepage widget, not core merchandising.
function seededRandom(seedStr) {
  let h = 1779033703 ^ seedStr.length;
  for (let i = 0; i < seedStr.length; i++) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let seed = h >>> 0;
  return () => {
    seed = Math.imul(seed ^ (seed >>> 15), seed | 1);
    seed ^= seed + Math.imul(seed ^ (seed >>> 7), seed | 61);
    return ((seed ^ (seed >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle(arr, rng) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export const CategoryMegaCard = ({ cat, index }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { slides } = cat;

  useEffect(() => {
    if (paused || slides.length < 2) return;
    const t = setInterval(() => {
      setActive((a) => (a + 1) % slides.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const prev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActive((a) => (a - 1 + slides.length) % slides.length);
  };
  const next = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActive((a) => (a + 1) % slides.length);
  };

  const slide = slides[active];

  // Only the slides a shopper can see load: the current one, the next (so
  // the crossfade has it ready) and any already shown. Every slide used to
  // be mounted with its background set, so a hidden slide still downloaded,
  // at full size: the whole category pool on every homepage visit,
  // including a 1.17 MB PNG (measured on the Phase 1 preview, 2026-10-13).
  const [shown, setShown] = useState(() => new Set([0, 1]));
  useEffect(() => {
    setShown((prev) => {
      const next = (active + 1) % Math.max(slides.length, 1);
      if (prev.has(active) && prev.has(next)) return prev;
      return new Set([...prev, active, next]);
    });
  }, [active, slides.length]);

  return (
    <div
      className="mega-card"
      style={{ "--i": index }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mega-slide">
        {slides.map((s, i) => (
          <div
            key={s.key}
            data-testid="mega-slide-img"
            className={`mega-slide-img${i === active ? " is-active" : ""}`}
            // A background cannot carry a srcset, so one ladder rung: 960 covers
            // the card at 2x on desktop and full-width on a phone.
            style={shown.has(i) ? { backgroundImage: `url(${imageUrl(s.image_url, 960)})` } : undefined}
          />
        ))}
        <div className="mega-slide-overlay" />

        <Link to={slide.to} className="mega-slide-link" aria-label={slide.label} />

        {/* No visible category or product name (owner, 2026-11-02): the
            photo and "See more" carry the card. The names stay in the
            links' accessible names. */}

        {slides.length > 1 && (
          <>
            <button type="button" className="mega-arrow mega-arrow-prev" onClick={prev} aria-label="Previous">‹</button>
            <button type="button" className="mega-arrow mega-arrow-next" onClick={next} aria-label="Next">›</button>
          </>
        )}

        <Link to={cat.to} className="mega-card-cta" aria-label={`See more ${cat.title}`}>See more →</Link>
      </div>
    </div>
  );
};

const ShopByCategory = () => {
  const [cards, setCards] = useState([]);

  useEffect(() => {
    let cancelled = false;

    fetchCategoryTree(false).then((tree) => {
      const departments = (Array.isArray(tree) ? tree : []).filter((c) => c.launch_active !== false);

      Promise.all([
        serverNow(),
        Promise.all(departments.map((dept) =>
          cachedFetch(`${API}/products?category=${dept.slug}&limit=24`)
            .then((products) => ({
              dept,
              products: (Array.isArray(products) ? products : []).filter(isInStock),
            }))
            .catch(() => ({ dept, products: [] }))
        )),
      ]).then(([now, results]) => {
        if (cancelled) return;

        const sessionId = getSessionId();
        const epoch = Math.floor(Date.now() / EPOCH_MS);
        const rng = seededRandom(`shop-by-category:${sessionId}:${epoch}`);

        // Build each department's full slide pool up front (union of
        // curated subcategory thumbnails — still good art, kept, not
        // replaced — and real product images), so selection below can rank
        // by what's actually available instead of guessing from a raw
        // product count.
        const withSlides = results.map(({ dept, products }) => {
          const subcatSlides = (dept.children || [])
            .filter((c) => c.image_url)
            .map((c) => ({
              key: `sub-${c.slug}`,
              label: c.name,
              image_url: c.image_url,
              to: `/category/${dept.slug}?sub=${c.slug}`,
            }));
          const productSlides = products
            .map((p) => ({
              key: `product-${p.id}`,
              label: p.name,
              image_url: p.image_url,
              to: `/product/${p.slug || p.id}`,
            }))
            .filter((s) => s.image_url);

          return {
            id: dept.slug,
            title: dept.name,
            to: `/category/${dept.slug}`,
            pool: [...subcatSlides, ...productSlides],
            isRich: products.length >= MIN_PRODUCTS_TO_FEATURE,
          };
        }).filter((c) => c.pool.length > 0);

        // Prefer departments with real variety (>= MIN_PRODUCTS_TO_FEATURE
        // distinct products), seeded-shuffled among themselves — but if
        // fewer than FEATURED_COUNT departments qualify (common on a
        // smaller/seeding-stage catalog), backfill the remaining slots from
        // whatever else has at least one usable image, rather than just
        // showing fewer cards. Always shows up to FEATURED_COUNT cards as
        // long as that many departments have any content at all.
        //
        // Thursday 14:00 to Sunday 23:59 (Nairobi, server clock) the 4th
        // card is always Drinks and the other three rotate among the rest.
        // If Drinks has nothing to show, all four rotate as usual.
        const drinks = isDrinksWindow(now) ? withSlides.find((c) => c.id === DRINKS_SLUG) : undefined;
        const pool = drinks ? withSlides.filter((c) => c !== drinks) : withSlides;
        const rich = seededShuffle(pool.filter((c) => c.isRich), rng);
        const thin = seededShuffle(pool.filter((c) => !c.isRich), rng);
        const featured = drinks
          ? [...[...rich, ...thin].slice(0, FEATURED_COUNT - 1), drinks]
          : [...rich, ...thin].slice(0, FEATURED_COUNT);

        const built = featured.map((c) => ({
          ...c,
          slides: seededShuffle(c.pool, rng).slice(0, MAX_SLIDES_PER_CARD),
        }));

        setCards(built);
      });
    });

    return () => { cancelled = true; };
  }, []);

  if (cards.length === 0) return null;

  return (
    <Reveal as="div" className="mega-grid reveal-children">
      {cards.map((cat, i) => (
        <CategoryMegaCard key={cat.id} cat={cat} index={i} />
      ))}
    </Reveal>
  );
};

export default ShopByCategory;
